import type {
  SubmissionField,
  SubmissionGroup,
  SubmissionItem,
  SubmissionSection,
} from "../../utils/originalSubmissionSections";
import type { SubmissionDocument } from "../../utils/originalSubmissionState";
import { DocumentLink } from "./DocumentLink";
import styles from "./OriginalSubmissionTab.module.css";

const STACK_THRESHOLD = 48;

function isStacked(field: SubmissionField): boolean {
  return (
    field.kind === "value" && (field.value.length > STACK_THRESHOLD || field.value.includes("\n"))
  );
}

function FieldValue({ field }: { field: SubmissionField }) {
  if (field.kind === "notCaptured") return <span className={styles.notCaptured}>Not captured</span>;
  if (field.kind === "notProvided") return <span className={styles.notProvided}>Not provided</span>;
  return <>{field.value}</>;
}

function FieldRow({ field }: { field: SubmissionField }) {
  const kind = isStacked(field) ? styles.stacked : styles.inline;
  return (
    <div className={`${styles.row} ${kind}`}>
      <span className={styles.label} title={field.fullLabel}>
        {field.label}
      </span>
      <span className={styles.value}>
        <FieldValue field={field} />
      </span>
    </div>
  );
}

function ItemRows({ item }: { item: SubmissionItem }) {
  return (
    <div className={styles.item}>
      {item.fields.map(field => (
        <FieldRow key={field.fullLabel} field={field} />
      ))}
    </div>
  );
}

function GroupRows({ group }: { group: SubmissionGroup }) {
  return (
    <div className={styles.group}>
      <h4 className={styles.subHeading}>{group.title}</h4>
      {group.items.map((item, index) => (
        <ItemRows key={`${group.title}-${index}`} item={item} />
      ))}
    </div>
  );
}

function isEmpty(section: SubmissionSection): boolean {
  return (
    section.fields.length === 0 &&
    section.groups.length === 0 &&
    section.notCapturedItemFields.length === 0
  );
}

function SectionBody({
  section,
  document,
}: {
  section: SubmissionSection;
  document: SubmissionDocument | null;
}) {
  return (
    <div className={styles.sectionBody}>
      {isEmpty(section) && <p className={styles.emptySection}>Nothing captured in this section</p>}
      {section.fields.map(field => (
        <FieldRow key={field.fullLabel} field={field} />
      ))}
      {section.groups.map(group => (
        <GroupRows key={group.title} group={group} />
      ))}
      {section.notCapturedItemFields.length > 0 && (
        <div className={`${styles.row} ${styles.stacked}`}>
          <span className={styles.label}>Not captured</span>
          <span className={styles.value}>
            <span className={styles.notCaptured}>{section.notCapturedItemFields.join(", ")}</span>
          </span>
        </div>
      )}
      {section.id === "metadata" && document && (
        <div className={styles.documentLink}>
          <DocumentLink document={document} />
        </div>
      )}
    </div>
  );
}

export function SubmissionSections({
  sections,
  document,
}: {
  sections: SubmissionSection[];
  document: SubmissionDocument | null;
}) {
  return (
    <div className={styles.sections}>
      {sections.map((section, index) => (
        <details key={section.id} className={styles.section} open={index === 0}>
          <summary className={styles.sectionSummary}>{section.title}</summary>
          <SectionBody section={section} document={document} />
        </details>
      ))}
    </div>
  );
}
